/**
 * Structured data for a blog post (2026-10-16). Plain JS with no browser or
 * React dependency, so Pack 2's server renderer can import the same builder
 * and the page and the raw HTML always agree.
 *
 * Replaces the old client block, which named the author as an Organization
 * ("Admin User" on production) and put the whole featuredImage OBJECT — on
 * most posts a multi-megabyte data URI — into `image`.
 */
import { SITE, BLOG_AUTHOR } from '../data/blogConfig';

const ORG_ID = SITE; // matches "@id" of the ProfessionalService block in public/index.html
const PERSON_ID = `${SITE}/about#thomas`;

function iso(d) {
  if (!d) return undefined;
  const t = new Date(d);
  return isNaN(t) ? undefined : t.toISOString();
}

/**
 * A Cloudinary delivery URL with exactly one transformation (any existing one
 * is replaced). Non-Cloudinary URLs come back unchanged; data: URIs as null.
 */
export function cloudinaryWith(url, transform) {
  if (!url || /^data:/i.test(url)) return null;
  const marker = '/image/upload/';
  const i = url.indexOf(marker);
  if (i === -1) return url;
  const head = url.slice(0, i + marker.length);
  let rest = url.slice(i + marker.length);
  const first = rest.split('/')[0];
  if (/(^|,)(c|w|h|q|f|g|ar|e)_/.test(first) && !/^v\d+$/.test(first)) rest = rest.slice(first.length + 1);
  return `${head}${transform}/${rest}`;
}

/** Cloudinary crops Google asks for (16:9, 4:3, 1:1). Non-Cloudinary URLs pass through once. */
export function imageVariants(url) {
  if (!url || /^data:/i.test(url)) return [];
  if (url.indexOf('/image/upload/') === -1) return [url];
  return [
    cloudinaryWith(url, 'c_fill,w_1600,h_900,q_auto,f_auto'),
    cloudinaryWith(url, 'c_fill,w_1200,h_900,q_auto,f_auto'),
    cloudinaryWith(url, 'c_fill,w_1200,h_1200,q_auto,f_auto')
  ];
}

/** The share-card size for a post (one fixed string: one derived file per image). */
export const POST_OG_TRANSFORM = 'c_fill,w_1200,h_630,q_auto,f_jpg';

export function blogPostingSchema(post) {
  const url = `${SITE}/blog/${post.slug}`;
  const author = post.authorProfile || BLOG_AUTHOR;
  const original = (post.featuredImageOriginal && post.featuredImageOriginal.url) || (post.featuredImage && post.featuredImage.url);
  const images = imageVariants(original);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: String(post.title || '').slice(0, 110),
    description: post.metaDescription || post.excerpt || undefined,
    ...(images.length ? { image: images } : {}),
    datePublished: iso(post.publishedAt),
    dateModified: iso(post.dateModified || post.publishedAt),
    author: {
      '@type': 'Person',
      '@id': PERSON_ID,
      name: author.displayName || BLOG_AUTHOR.displayName,
      jobTitle: author.jobTitle || BLOG_AUTHOR.jobTitle,
      url: author.url || BLOG_AUTHOR.url
    },
    publisher: {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: 'Utah Realty Photos',
      logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` }
    },
    ...(post.wordCount ? { wordCount: post.wordCount } : {}),
    ...(post.category ? { articleSection: post.category } : {})
  };
}

/** The Person every byline names — printed on /about (page and renderer). */
export function personSchema({ image } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': PERSON_ID,
    name: BLOG_AUTHOR.displayName,
    jobTitle: BLOG_AUTHOR.jobTitle,
    url: `${SITE}/about`,
    ...(image ? { image } : {}),
    worksFor: { '@type': 'ProfessionalService', '@id': ORG_ID, name: 'Utah Realty Photos', url: SITE },
    homeLocation: { '@type': 'Place', name: 'Eagle Mountain, Utah' },
    knowsAbout: ['Real estate photography', 'Listing video', 'Matterport 3D tours', 'Virtual staging', 'Virtual twilight photos']
  };
}

/** Only when the FAQ is visibly on the page (BlogArticle renders it). */
export function faqSchema(post) {
  const faq = (post.faq || []).filter(f => f && f.q && f.a);
  if (!faq.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }))
  };
}

export function breadcrumbItems(post) {
  return [
    { name: 'Home', url: `${SITE}/` },
    { name: 'Blog', url: `${SITE}/blog` },
    { name: post.title, url: `${SITE}/blog/${post.slug}` }
  ];
}
